'use client';

import { useCallback, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  useGetOnboardingSessionQuery,
  useSubmitOnboardingStepMutation,
} from '@/api/features/onboarding';
import type { OnboardingSession, OnboardingStep } from '@/api/features/onboarding';
import { useGetUserProfileQuery } from '@/api/features/users';
import { safeRemoveItem } from '@/shared/utils/safeStorage';

/**
 * Hook result type for useOnboardingSession
 */
export interface UseOnboardingSessionResult {
  /** The current onboarding session (RTK Query cache - single source of truth) */
  session: OnboardingSession | undefined;
  /** Whether the session is loading */
  isLoading: boolean;
  /** Whether the session is being refetched */
  isFetching: boolean;
  /** Any error that occurred (session or profile) */
  error: unknown;
  /** Profile-specific error */
  profileError: unknown;
  /** Whether a step submission is in progress */
  isSubmitting: boolean;
  /** Submit a step atomically with idempotency support */
  submitStep: (step: OnboardingStep, data: Record<string, unknown>) => Promise<{ nextRoute: string }>;
  /** Refetch the session */
  refetch: () => Promise<unknown>;
  /** Refetch the user profile */
  refetchProfile: () => void;
  /** User profile data */
  userProfile: { organizationId: string; email: string; firstName?: string; lastName?: string } | undefined;
  /** Whether the user profile is loading */
  isLoadingProfile: boolean;
}

/**
 * Map backend steps to frontend routes
 * Exported for use in signup and other pages that need step routing
 */
export const STEP_TO_ROUTE_MAP: Record<string, string> = {
  'account_creation': '/signup',
  'company_info': '/company-info',
  'payment_setup': '/payment-setup',
  'setup_complete': '/setup-complete',
  'demo': '/dashboard',
  'awaiting_setup': '/setup-complete',
  'account_qualified': '/dashboard',
  // Legacy steps redirect to payment-setup for graceful migration
  'number_selection': '/payment-setup',
  'new_number_business_info': '/payment-setup',
  'new_number_address': '/payment-setup',
  'new_number_authorized_rep': '/payment-setup',
  'new_number_messaging': '/payment-setup',
  'new_number_documents': '/payment-setup',
  'new_number_review': '/payment-setup',
  'twilio_trust_hub': '/payment-setup',
  'twilio_brand_registration': '/payment-setup',
  'twilio_campaign_registration': '/payment-setup',
  'twilio_number_purchase': '/payment-setup',
  'existing_number_eligibility': '/payment-setup',
  'existing_number_hosting': '/payment-setup',
  'existing_number_verification': '/payment-setup',
  'existing_number_loa': '/payment-setup',
  'existing_number_carrier': '/payment-setup',
  'existing_number_activation': '/payment-setup',
  'integration_setup': '/payment-setup',
  'contact_import': '/payment-setup',
  'agency_config': '/payment-setup',
};

/**
 * Map frontend routes to expected backend steps
 * Used for step validation - ensures user is on the correct page for their current step
 */
export const ROUTE_TO_STEP_MAP: Record<string, string[]> = {
  '/signup': ['account_creation'],
  '/company-info': ['company_info'],
  '/payment-setup': ['payment_setup'],
  '/setup-complete': ['setup_complete', 'awaiting_setup', 'demo'],
  '/dashboard': ['account_qualified', 'completed'],
};

/**
 * Options for useOnboardingSession hook
 */
export interface UseOnboardingSessionOptions {
  /** Skip all queries until this becomes false. Use with useOnboardingAuthReady. */
  skip?: boolean;
}

/**
 * Simplified onboarding session hook that uses RTK Query cache as the single source of truth.
 *
 * Design principles:
 * - **Single source of truth**: RTK Query cache only, no local state duplication
 * - **Form-first**: Simple form submission, no auto-save complexity
 * - **Idempotent operations**: All mutations safely retryable
 *
 * Usage:
 * ```tsx
 * const { ready: authReady } = useOnboardingAuthReady();
 * const { session, isLoading, submitStep, isSubmitting } = useOnboardingSession({ skip: !authReady });
 *
 * const handleSubmit = async (data: CompanyInfoData) => {
 *   try {
 *     const { next_route } = await submitStep('company_info', data);
 *     router.push(next_route);
 *   } catch (error) {
 *     // Handle error
 *   }
 * };
 * ```
 */
export function useOnboardingSession(options?: UseOnboardingSessionOptions): UseOnboardingSessionResult {
  const { skip = false } = options || {};
  const router = useRouter();
  const pathname = usePathname();

  // Get user profile first (needed for organizationId)
  // Skip until auth is ready to prevent race conditions after signup/login
  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    isFetching: isFetchingProfile,
    error: profileError,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useGetUserProfileQuery(undefined, {
    skip,
    refetchOnMountOrArgChange: true,
  });

  // Handle profile errors - redirect to login
  useEffect(() => {
    if (isProfileError && profileError) {
      const error = profileError as { status?: number; originalStatus?: number };
      const status = error?.status ?? error?.originalStatus;
      if (status === 401 || status === 403 || status === 404) {
        safeRemoveItem('auth_state_cache');
        safeRemoveItem('onboarding_user_id');
        router.replace(`/login?returnUrl=${encodeURIComponent(pathname)}&message=session_expired`);
      }
    }
  }, [isProfileError, profileError, pathname, router]);

  // Get organizationId from profile
  const organizationId = (userProfile as { organizationId?: string } | undefined)?.organizationId;

  // Get onboarding session (skip until we have organization_id)
  const {
    data: session,
    isLoading: isSessionLoading,
    isFetching: isSessionFetching,
    error: sessionError,
    refetch,
  } = useGetOnboardingSessionQuery(undefined, {
    skip: !organizationId,
    refetchOnMountOrArgChange: true,
  });

  // Submit step mutation
  const [submitStepMutation, { isLoading: isSubmitting }] = useSubmitOnboardingStepMutation({
    fixedCacheKey: 'submit-step-fixed',
  });

  // Generate unique idempotency key for each submission
  const generateIdempotencyKey = useCallback(() => {
    return crypto.randomUUID();
  }, []);

  /**
   * Submit a step atomically with idempotency support.
   *
   * This method:
   * 1. Generates an idempotency key for safe retries
   * 2. Sends data to the unified step endpoint
   * 3. Backend handles validation, step completion, and state update atomically
   * 4. Returns the next route for navigation
   *
   * @param step - The step to submit (e.g., 'company_info', 'payment_setup')
   * @param data - The step data to submit
   * @returns Object with next_route for navigation
   */
  const submitStep = useCallback(
    async (step: OnboardingStep, data: Record<string, unknown>): Promise<{ nextRoute: string }> => {
      // Generate new idempotency key for this submission
      const idempotencyKey = generateIdempotencyKey();

      const result = await submitStepMutation({
        step,
        data,
        idempotencyKey: idempotencyKey,
      }).unwrap();

      return { nextRoute: result.nextRoute };
    },
    [submitStepMutation, generateIdempotencyKey]
  );

  // Determine overall loading state
  // Note: isLoading is for initial load only (when no cached data exists)
  // isFetching indicates background refetches with cached data
  const isLoading = isLoadingProfile || (!!organizationId && isSessionLoading);
  const isFetching = isFetchingProfile || isSessionFetching;

  // Auto-redirect based on session state - enforces step order
  useEffect(() => {
    if (!session || isLoading) return;

    // If onboarding is completed, always exit to dashboard
    if (session.status === 'completed') {
      if (pathname !== '/dashboard' && !pathname.startsWith('/dashboard')) {
        router.replace('/dashboard');
      }
      return;
    }

    const currentStep = session.currentStep;
    const expectedRoute = STEP_TO_ROUTE_MAP[currentStep] || '/dashboard';

    // Check if current route is valid for the current step
    const validStepsForRoute = ROUTE_TO_STEP_MAP[pathname];
    const isOnValidRoute = validStepsForRoute?.includes(currentStep);

    // If we're already on a valid route for this step, no redirect needed
    if (isOnValidRoute) {
      return;
    }

    // Skip redirect for signup (handled separately) and dashboard
    if (pathname === '/signup' || pathname.startsWith('/dashboard')) {
      return;
    }

    // Redirect to correct step if we're on the wrong page
    if (pathname !== expectedRoute) {
      router.replace(expectedRoute);
    }
  }, [session, isLoading, pathname, router]);

  return {
    session,
    isLoading,
    isFetching,
    error: sessionError || profileError,
    profileError,
    isSubmitting,
    submitStep,
    refetch,
    refetchProfile,
    userProfile: userProfile as UseOnboardingSessionResult['userProfile'],
    isLoadingProfile: isLoadingProfile || isFetchingProfile,
  };
}
